// client/src/components/pages/ErrorDetection/ErrorCard.tsx (converted from .js, ST-00216)
/**
 * Error Detection Card
 * Displays a single error detection rule with severity status,
 * error count, thresholds, and action buttons.
 */

import { useState, useEffect, useRef } from 'react';
import {
  Eye,
  Bell,
  RefreshCw,
  Trash2,
  MoreVertical,
  ChevronRight,
  Pencil,
  Flame,
  Mail,
  History,
} from 'lucide-react';
import { getSeverityConfig as getSeverityConfigJs } from './severityConfig';
import type {
  ErrorDetectionResult,
  SeverityStyle,
  SeverityThresholds,
} from '../../../types/clientPages/errorDetection';

/**
 * Every row the latest run found. /results sends only a sample of them (WBE-12), so the
 * count comes from error_details_total / error_count, not the sample's length.
 */
const totalErrorRows = (r: ErrorDetectionResult): number => {
  if (typeof r.error_details_total === 'number') return r.error_details_total;
  if (typeof r.error_count === 'number' && r.error_count >= 0) return r.error_count;
  return (r.error_details || []).length;
};

// Typed view of the (still JavaScript) severity table: unknown levels fall back to 'none'.
const getSeverityConfig = getSeverityConfigJs as unknown as (severity: string) => SeverityStyle;

interface ErrorCardProps {
  errorResult: ErrorDetectionResult;
  onViewDetails: (errorResult: ErrorDetectionResult) => void;
  onSetupNotification: (errorResult: ErrorDetectionResult) => void;
  onRunCheck: (ruleId: number) => void;
  onDeleteRule: (ruleId: number) => void;
  onEditRule: (errorResult: ErrorDetectionResult) => void;
  onViewAlerts?: (errorResult: ErrorDetectionResult) => void;
  isRunning?: boolean;
  delay?: number;
  canManage?: boolean;
}

/* ─── Severity Badge ─────────────────────────── */
const SeverityBadge = ({ severity }: { severity: string }) => {
  const config = getSeverityConfig(severity);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${config.bg} ${config.text} ${config.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

/* ─── Severity Icon ──────────────────────────── */
const SeverityIcon = ({
  severity,
  className = 'w-5 h-5',
}: {
  severity: string;
  className?: string;
}) => {
  const config = getSeverityConfig(severity);
  const Icon = config.icon;
  return <Icon className={`${className} ${config.text}`} />;
};

/* ─── Main ErrorCard ─────────────────────────── */
const ErrorCard = ({
  errorResult,
  onViewDetails,
  onSetupNotification,
  onRunCheck,
  onDeleteRule,
  onEditRule,
  onViewAlerts,
  isRunning = false,
  delay = 0,
  // false hides Edit / Delete / Run (they need errors.manage — ST-00214)
  canManage = true,
}: ErrorCardProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const severity = errorResult.severity_level || 'none';
  const config = getSeverityConfig(severity);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
        setConfirmDelete(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Parse thresholds safely
  const thresholds: SeverityThresholds = errorResult.severity_thresholds || {};
  const currentStreak = errorResult.current_streak || 0;
  const breachDaysIn30 = errorResult.breach_days_in30 || 0;
  const streakDaysToAlert = errorResult.streak_days_to_alert || 2;
  const isAlertActive = currentStreak >= streakDaysToAlert;
  const lastAlertSentAt = errorResult.last_alert_sent_at;

  return (
    <div
      className="group relative bg-surface rounded-xl border border-slate-200 hover:shadow-card-hover transition-all duration-300 animate-fadeIn overflow-hidden"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Severity accent bar */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${config.accent}`} />

      <div className="p-5 pt-4">
        {/* Header row */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 ${config.bg} ${config.border} border`}
            >
              <SeverityIcon severity={severity} />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-slate-900 truncate">
                {errorResult.rule_name}
              </h3>
              <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">
                {errorResult.description || 'No description'}
              </p>
            </div>
          </div>

          {/* Actions menu */}
          {canManage && (
            <div className="relative ml-2" ref={menuRef}>
              <button
                onClick={() => {
                  setShowMenu(!showMenu);
                  setConfirmDelete(false);
                }}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <MoreVertical className="w-4 h-4" />
              </button>

              {showMenu && (
                <div className="absolute right-0 top-8 w-44 bg-surface border border-slate-200 rounded-lg shadow-lg z-20 py-1 animate-fadeIn">
                  {!confirmDelete ? (
                    <>
                      <button
                        onClick={() => {
                          setShowMenu(false);
                          onEditRule(errorResult);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Edit Rule
                      </button>
                      <button
                        onClick={() => setConfirmDelete(true)}
                        className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete Rule
                      </button>
                    </>
                  ) : (
                    <div className="px-3 py-2">
                      <p className="text-xs text-slate-600 mb-2">Delete this rule?</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowMenu(false);
                            setConfirmDelete(false);
                            onDeleteRule(errorResult.rule_id);
                          }}
                          className="flex-1 px-2 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setConfirmDelete(false)}
                          className="flex-1 px-2 py-1.5 text-xs font-semibold bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Metrics row */}
        <div className={`rounded-lg p-3 mb-3 ${config.bg} border ${config.border}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">
              Error Count
            </span>
            <SeverityBadge severity={severity} />
          </div>
          <p
            className={`text-2xl font-bold tabular-nums ${config.text}`}
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
          >
            {errorResult.error_count === -1 ? '—' : errorResult.error_count || 0}
          </p>
          {errorResult.error_count === -1 && errorResult.error_details?.[0]?.Error && (
            <p
              className="text-[10px] text-purple-600 mt-1 truncate"
              title={errorResult.error_details[0].Error}
            >
              {errorResult.error_details[0].Error}
            </p>
          )}

          {/* Threshold indicators */}
          {(thresholds.warning || thresholds.critical || thresholds.armageddon) && (
            <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                {thresholds.warning}+
              </span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                {thresholds.critical}+
              </span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                {thresholds.armageddon}+
              </span>
            </div>
          )}
        </div>

        {/* Streak + alert status row */}
        {(currentStreak > 0 || breachDaysIn30 > 0 || lastAlertSentAt) && (
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {currentStreak > 0 && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold border ${
                  isAlertActive
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}
                title={`Consecutive breach days. Alerts fire at ${streakDaysToAlert}.`}
              >
                <Flame className="w-3 h-3" />
                Day {currentStreak} / {streakDaysToAlert}
              </span>
            )}
            {breachDaysIn30 > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-slate-50 text-slate-600 border border-slate-200">
                {breachDaysIn30} breach day{breachDaysIn30 === 1 ? '' : 's'} / 30
              </span>
            )}
            {lastAlertSentAt && (
              <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200"
                title={`Last alert email sent ${new Date(lastAlertSentAt).toLocaleString()}`}
              >
                <Mail className="w-3 h-3" />
                Alerted {new Date(lastAlertSentAt).toLocaleDateString()}
              </span>
            )}
          </div>
        )}

        {/* Last checked */}
        {errorResult.checked_at && (
          <p className="text-[10px] text-slate-400 mb-3">
            Checked {new Date(errorResult.checked_at).toLocaleString()}
          </p>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => onViewDetails(errorResult)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-surface text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
          >
            <Eye className="w-3.5 h-3.5" />
            Details
          </button>
          <button
            onClick={() => onSetupNotification(errorResult)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-surface text-xs font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
          >
            <Bell className="w-3.5 h-3.5" />
            Notify
          </button>
          {onViewAlerts && (
            <button
              onClick={() => onViewAlerts(errorResult)}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 bg-surface text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition-all"
              title="Alert log"
            >
              <History className="w-3.5 h-3.5" />
            </button>
          )}
          {canManage && (
            <button
              onClick={() => onRunCheck(errorResult.rule_id)}
              disabled={isRunning}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 bg-surface text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition-all disabled:opacity-50"
              title="Run Check"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isRunning ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>

        {/* Error preview */}
        {errorResult.error_details && errorResult.error_details.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <button
              onClick={() => onViewDetails(errorResult)}
              className="w-full text-left group/preview"
            >
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
                Sample ({Math.min(2, errorResult.error_details.length)} of{' '}
                {totalErrorRows(errorResult).toLocaleString()})
              </p>
              <div className="space-y-1">
                {errorResult.error_details.slice(0, 2).map((err, i) => (
                  <div
                    key={i}
                    className="text-[11px] bg-slate-50 rounded-md px-2.5 py-1.5 text-slate-600 truncate"
                  >
                    {Object.entries(err)
                      .slice(0, 3)
                      .map(([k, v]) => (
                        <span key={k} className="mr-2">
                          <span className="font-medium text-slate-500">{k}:</span>{' '}
                          <span className="text-slate-800">
                            {v !== null && v !== undefined ? String(v) : '-'}
                          </span>
                        </span>
                      ))}
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-1 mt-1.5 text-[10px] text-primary-600 font-medium opacity-0 group-hover/preview:opacity-100 transition-opacity">
                View all details <ChevronRight className="w-3 h-3" />
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export { SeverityBadge, SeverityIcon, totalErrorRows };
export default ErrorCard;
